import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  fetchFyReturnPdfs,
  fetchMonthReturnPdf,
  getFyReturnStatus,
  getFyReturnZip,
  getStoredReturnPdf,
  PdfReturn,
  ReturnPdfMonth,
} from "api/services/gstr/returnReports";
import { blobErrorMessage, fileNameFromHeaders, saveBlob } from "utils/blobFile";
import { snack } from "components/toast";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export const RETURN_LABEL: Record<PdfReturn, string> = { GSTR1: "GSTR-1/IFF", GSTR3B: "GSTR-3B" };
const STATE_CHIP: Record<ReturnPdfMonth["state"], { label: string; color: string; bg: string }> = {
  // the return's filing status; when its PDF was fetched shows in "Data Fetched On"
  fetched: { label: "Filed", color: "#047857", bg: "#D1FAE5" },
  stored: { label: "Filed", color: "#047857", bg: "#D1FAE5" },
  not_filed: { label: "Not filed", color: "#B45309", bg: "#FEF3C7" },
  not_applicable: { label: "NA", color: "#475569", bg: "#F1F5F9" },
  failed: { label: "Failed", color: "#B91C1C", bg: "#FEE2E2" },
};
const SYSGEN_TEXT: Record<"not_available" | "failed", { label: string; color: string }> = {
  not_available: { label: "Not available", color: "#B45309" },
  failed: { label: "Failed", color: "#B91C1C" },
};

/** Apr..Mar of the FY ("2025-26") as MMYYYY, with whether the month is over (a running month has no filed return). */
function fyMonths(fy: string) {
  const start = Number(fy.slice(0, 4));
  const now = new Date();
  return [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3].map((m) => {
    const y = m >= 4 ? start : start + 1;
    const over = y < now.getFullYear() || (y === now.getFullYear() && m < now.getMonth() + 1);
    return { period: `${String(m).padStart(2, "0")}${y}`, label: `${MONTHS[m - 1]} ${y}`, over };
  });
}

/** dd/mm/yyyy -> yyyymmdd (comparable), null when not a date. */
function ymd(dmy: string | null | undefined) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dmy || "");
  return m ? Number(m[3] + m[2] + m[1]) : null;
}

/** Filed after the due date. */
function filedLate(row?: ReturnPdfMonth) {
  const filed = ymd(row?.filed_date);
  const due = ymd(row?.due_date);
  return filed !== null && due !== null && filed > due;
}

/** dd/mm/yyyy hh:mm of when the PDF was fetched from the GST portal. */
function fetchedOn(iso: string) {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

interface Props {
  open: boolean;
  onClose: () => void;
  credentialId: number; // gstr_credentials.id of the client's GST login
  clientName: string;
  gstin?: string;
  ret: PdfReturn;
  fy: string;
}

const ReturnReportsDialog = ({ open, onClose, credentialId, clientName, gstin, ret, fy }: Props) => {
  const months = useMemo(() => fyMonths(fy), [fy]);
  const [rows, setRows] = useState<Record<string, ReturnPdfMonth>>({});
  const [loadingFy, setLoadingFy] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [zipping, setZipping] = useState(false);
  const [preview, setPreview] = useState<{ url: string; label: string; blob: Blob; fileName: string } | null>(null);
  const label = RETURN_LABEL[ret];
  const withSysGen = ret === "GSTR3B"; // GSTR-3B: also the portal's "System generated GSTR-3B" of each filed month

  // a new client / return / FY: show the months already downloaded earlier straight away (no portal login)
  useEffect(() => {
    let cancelled = false;
    setRows({});
    setPreview(null);
    setLoadingStatus(true);
    getFyReturnStatus(credentialId, { ret, fy })
      .then((res) => {
        if (!cancelled) setRows(Object.fromEntries(res.months.map((m) => [m.period, m])));
      })
      .catch(async (err) => {
        if (!cancelled) snack.error(await blobErrorMessage(err, `Could not load available ${RETURN_LABEL[ret]} PDFs.`));
      })
      .finally(() => {
        if (!cancelled) setLoadingStatus(false);
      });
    return () => {
      cancelled = true;
    };
  }, [credentialId, ret, fy]);
  useEffect(() => () => { if (preview) window.URL.revokeObjectURL(preview.url); }, [preview]);

  const getAllMonths = async () => {
    setLoadingFy(true);
    try {
      const res = await fetchFyReturnPdfs(credentialId, { ret, fy });
      setRows(Object.fromEntries(res.months.map((m) => [m.period, m])));
    } catch (err) {
      snack.error(await blobErrorMessage(err, `Could not get ${label} PDFs.`));
    } finally {
      setLoadingFy(false);
    }
  };

  const retryMonth = async (period: string) => {
    setBusyRow(period);
    try {
      const res = await fetchMonthReturnPdf(credentialId, { ret, period, refresh: true });
      const h = res.headers;
      // GSTR-3B: the system generated PDF is built with it; its row status comes from the stored files
      const sysgen = withSysGen
        ? (await getFyReturnStatus(credentialId, { ret, fy }).catch(() => null))?.months.find((m) => m.period === period)?.system_generated ?? null
        : null;
      setRows((r) => ({
        ...r,
        [period]: {
          period,
          state: "fetched",
          kind: (h["x-pdf-kind"] as any) || null,
          message: null,
          fetched_at: new Date().toISOString(),
          arn: h["x-pdf-arn"] || null,
          filed_date: h["x-pdf-filed-date"] || null,
          due_date: h["x-pdf-due-date"] || null,
          system_generated: sysgen,
        },
      }));
    } catch (err) {
      const message = await blobErrorMessage(err, "Could not get the PDF.");
      setRows((r) => ({
        ...r,
        [period]: {
          period,
          state: /not filed/i.test(message) ? "not_filed" : "failed",
          kind: null,
          message,
          fetched_at: null,
          arn: null,
          filed_date: null,
          due_date: r[period]?.due_date ?? null,
          system_generated: null,
        },
      }));
    } finally {
      setBusyRow(null);
    }
  };

  const openStored = async (period: string, monthLabel: string, download: boolean, systemGenerated = false) => {
    setBusyRow(period);
    try {
      const res = await getStoredReturnPdf(credentialId, { ret, period, systemGenerated });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const suffix = systemGenerated ? "_SystemGenerated" : "";
      const fileName = fileNameFromHeaders(res.headers, `${ret}_${gstin || ""}_${period}${suffix}.pdf`);
      if (download) saveBlob(blob, fileName);
      else
        setPreview({
          url: window.URL.createObjectURL(blob),
          label: systemGenerated ? `${monthLabel} — System generated GSTR-3B` : monthLabel,
          blob,
          fileName,
        });
    } catch (err) {
      snack.error(await blobErrorMessage(err, "PDF not found."));
    } finally {
      setBusyRow(null);
    }
  };

  const downloadZip = async () => {
    setZipping(true);
    try {
      const res = await getFyReturnZip(credentialId, { ret, fy });
      saveBlob(new Blob([res.data], { type: "application/zip" }), fileNameFromHeaders(res.headers, `${ret}_${gstin || ""}_${fy}.zip`));
    } catch (err) {
      snack.error(await blobErrorMessage(err, "No PDFs available for this FY yet."));
    } finally {
      setZipping(false);
    }
  };

  // per chip label (stored and fetched are both "Filed")
  const counts = Object.values(rows).reduce<Record<string, number>>((acc, r) => {
    const l = STATE_CHIP[r.state].label;
    return { ...acc, [l]: (acc[l] ?? 0) + 1 };
  }, {});
  const countChips = Object.values(STATE_CHIP).filter((c, i, all) => counts[c.label] && all.findIndex((x) => x.label === c.label) === i);
  const isAvailable = (period: string) => rows[period]?.state === "stored" || rows[period]?.state === "fetched";
  const sysGenAvailable = (period: string) => ["stored", "fetched"].includes(rows[period]?.system_generated?.state ?? "");
  // done = filed PDF there and, for GSTR-3B, its system generated PDF too (or the portal has none)
  // a quarterly filer's 1st/2nd month of a filed quarter has no return of its own: nothing to get
  const isNa = (period: string) => rows[period]?.state === "not_applicable";
  const isDone = (period: string) =>
    isNa(period) ||
    (isAvailable(period) && (!withSysGen || sysGenAvailable(period) || rows[period]?.system_generated?.state === "not_available"));
  const overMonths = months.filter((m) => m.over);
  const availableCount = overMonths.filter((m) => isDone(m.period) && !isNa(m.period)).length;
  const allAvailable = overMonths.length > 0 && overMonths.every((m) => isDone(m.period));
  const columns = [
    "Month",
    "Status",
    "Due Date",
    ...(withSysGen ? ["System Generated GSTR-3B (PDF)"] : []),
    "Date of Filing",
    "Acknowledgement # (ARN)",
    `${label} Filed Return (PDF)`,
    "Data Fetched On",
  ];  const fetchLabel = allAvailable
    ? "All months available"
    : availableCount
      ? `Get remaining ${label} months`
      : `Get ${label} for all months`;

  return (
    <Dialog open={open} onClose={loadingFy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {clientName}
            {gstin ? ` · ${gstin}` : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label} Filed PDFs — FY {fy}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={loadingFy}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {preview ? (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => setPreview(null)} sx={{ textTransform: "none" }}>
                Back to months
              </Button>
              <Typography fontWeight={600}>{preview.label}</Typography>
              <Button startIcon={<DownloadOutlinedIcon />} onClick={() => saveBlob(preview.blob, preview.fileName)} sx={{ textTransform: "none" }}>
                Download
              </Button>
            </Box>
            <Box component="iframe" title={`${label} PDF`} src={preview.url} sx={{ width: "100%", height: "70vh", border: "1px solid #E2E8F0", borderRadius: 1 }} />
          </Box>
        ) : (
          <>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
              <Button
                variant="contained"
                disableElevation
                startIcon={loadingFy ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadOutlinedIcon />}
                disabled={loadingFy || loadingStatus || allAvailable}
                onClick={getAllMonths}
                sx={{ textTransform: "none" }}
              >
                {loadingFy ? "Fetching from GST portal…" : fetchLabel}
              </Button>
              <Button
                variant="outlined"
                startIcon={zipping ? <CircularProgress size={16} /> : <FolderZipOutlinedIcon />}
                disabled={zipping || loadingFy}
                onClick={downloadZip}
                sx={{ textTransform: "none" }}
              >
                Download all (ZIP)
              </Button>
              <Box flexGrow={1} />
              {countChips.map((c) => (
                <Chip key={c.label} size="small" label={`${c.label}: ${counts[c.label]}`} sx={{ fontWeight: 600, color: c.color, bgcolor: c.bg }} />
              ))}
            </Box>
            {loadingFy && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                One GST portal login, then month by month — usually under a minute. Please keep this window open.
              </Typography>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((h) => (
                    <TableCell
                      key={h}
                      align={h.endsWith("(PDF)") ? "center" : "left"}
                      sx={{ fontWeight: 600, bgcolor: "#F8FAFC" }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {months.map((m) => {
                  const row = rows[m.period];
                  const chip = row ? STATE_CHIP[row.state] : null;
                  const available = isAvailable(m.period);
                  return (
                    <TableRow key={m.period} hover>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>{m.label}</TableCell>
                      <TableCell>
                        {!m.over ? (
                          <Typography variant="body2" color="text.disabled">Month not over</Typography>
                        ) : chip ? (
                          <Tooltip title={row?.message ?? ""}>
                            <Chip size="small" label={chip.label} sx={{ fontWeight: 600, color: chip.color, bgcolor: chip.bg }} />
                          </Tooltip>
                        ) : loadingFy || loadingStatus ? (
                          <CircularProgress size={14} />
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2">{row?.due_date ?? ""}</Typography>
                      </TableCell>
                      {withSysGen && (
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          {available && sysGenAvailable(m.period) ? (
                            <>
                              <Tooltip title="View system generated GSTR-3B">
                                <IconButton size="small" disabled={busyRow === m.period} onClick={() => openStored(m.period, m.label, false, true)}>
                                  <VisibilityOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download system generated GSTR-3B">
                                <IconButton size="small" disabled={busyRow === m.period} onClick={() => openStored(m.period, m.label, true, true)}>
                                  <DownloadOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : available && row?.system_generated && row.system_generated.state in SYSGEN_TEXT ? (
                            <Tooltip title={row.system_generated.message ?? ""}>
                              <Typography variant="body2" sx={{ color: SYSGEN_TEXT[row.system_generated.state as keyof typeof SYSGEN_TEXT].color }}>
                                {SYSGEN_TEXT[row.system_generated.state as keyof typeof SYSGEN_TEXT].label}
                              </Typography>
                            </Tooltip>
                          ) : available ? (
                            <Typography variant="body2" color="text.disabled">Not fetched yet</Typography>
                          ) : null}
                        </TableCell>
                      )}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {filedLate(row) ? (
                          <Tooltip title={`Filed after the due date (${row?.due_date})`}>
                            <Typography variant="body2" sx={{ color: "#DC2626", fontWeight: 600 }}>{row?.filed_date}</Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2">{row?.filed_date ?? ""}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2">{row?.arn ?? ""}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {busyRow === m.period ? (
                          <CircularProgress size={16} />
                        ) : (
                          <>
                            {available && (
                              <>
                                <Tooltip title="View">
                                  <IconButton size="small" onClick={() => openStored(m.period, m.label, false)}>
                                    <VisibilityOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton size="small" onClick={() => openStored(m.period, m.label, true)}>
                                    <DownloadOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {row && !available && !isNa(m.period) && m.over && (
                              <Tooltip title="Try this month again">
                                <IconButton size="small" disabled={loadingFy} onClick={() => retryMonth(m.period)}>
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" color="text.secondary">
                          {row?.fetched_at ? fetchedOn(row.fetched_at) : ""}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {!loadingFy && !loadingStatus && !allAvailable && (
              <Typography variant="body2" color="text.secondary" mt={1.5}>
                {availableCount
                  ? `${availableCount} month(s) already downloaded. "Get remaining" fetches only the rest from the GST portal.`
                  : `Nothing downloaded yet for FY ${fy}. Click "Get ${label} for all months".`}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReturnReportsDialog;
